import { z } from 'zod'
import { AnswerModeSchema, ModelAliasSchema, RiskClassSchema } from './enums.ts'
import { ResolvedPolicyRefSchema } from './policies.ts'

/**
 * An agent, as stored configuration.
 *
 * Every field is optional here because this schema describes a LAYER, not a finished
 * agent. Layers merge least-specific to most-specific — environment defaults, then the
 * agent's own `'*'` row, then at most one group row — and only the merged result has to
 * be complete. `ResolvedAgentSchema` at the bottom of this file is that completed shape,
 * and it is the one the runtime consumes.
 *
 * The split matters in practice: a group override that only wants to raise a budget
 * should not have to restate the prompt, and a prompt edit should not silently drop
 * someone else's tool grant.
 */

export const AGENT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}$/
export const AgentIdSchema = z
  .string()
  .regex(AGENT_ID_PATTERN, 'agent id must be lowercase alphanumeric with dashes, 2-63 characters')

/** `'*'` is the agent's own default layer; anything else names a group. */
export const AgentScopeIdSchema = z.string().min(1).max(64)

export const DEFAULT_AGENT_ID = 'default'
export const DEFAULT_AGENT_SCOPE = '*'

/**
 * Which tools the agent may use.
 *
 * `builtin` names compiled-in tools; `mcp` names server-qualified capabilities
 * (`server/tool`). Both are allow-lists and both default to "none" rather than "all" —
 * an agent that has not been granted a tool cannot reach it however the model is
 * prompted, which is the intersection rule the skills PRD (SKL-006) asks for.
 */
export const AgentToolGrantSchema = z.object({
  builtin: z.array(z.string().min(1)).default([]),
  mcp: z.array(z.string().min(1)).default([])
})
export type AgentToolGrant = z.infer<typeof AgentToolGrantSchema>

export const AgentBudgetSchema = z.object({
  maxSteps: z.number().int().positive().optional(),
  maxModelCalls: z.number().int().positive().optional(),
  maxToolCalls: z.number().int().positive().optional(),
  maxDelegations: z.number().int().nonnegative().optional(),
  maxCostUsd: z.number().nonnegative().optional(),
  maxWallClockMs: z.number().int().positive().optional()
})
export type AgentBudget = z.infer<typeof AgentBudgetSchema>

/**
 * Which knowledge bases this agent is pinned to — and nothing else.
 *
 * `strategy`, `topK` and `minScore` used to live here. They were stored, merged, resolved
 * and editable, and no runtime code ever read them (PRD-18 F-4); they are now RuntimePolicy
 * values (`packages/contracts/src/policies.ts`) so the number an operator sets is the number
 * a run uses. What stays is the one retrieval field that is genuinely the agent's own: the
 * attachment, which the orchestrator enforces as the outer bound of every search.
 */
export const AgentRetrievalSchema = z.object({
  /** Empty means "every source this user may read". */
  scopeIds: z.array(z.uuid()).optional()
})

export const AgentMemorySchema = z.object({
  read: z.boolean().optional(),
  /** `propose` lets the agent suggest a memory that a human confirms. */
  write: z.enum(['off', 'propose', 'auto']).optional()
})

/**
 * Delegation to remote agents (A2A).
 *
 * `remoteAgents` is an allow-list of registry slugs. Depth and parallelism are capped here
 * as well as per-grant because the grant governs "may this agent be asked at all" and this
 * governs "how much of this run's budget may go to asking".
 */
export const AgentDelegationSchema = z.object({
  remoteAgents: z.array(z.string().min(1)).default([]),
  maxDepth: z.number().int().min(0).max(3).optional(),
  maxParallel: z.number().int().min(1).max(8).optional()
})

export const AgentBodySchema = z
  .object({
    systemPrompt: z.string().optional(),
    /**
     * Prepended to the prompt and CONCATENATED across layers rather than overridden, so a
     * group can add a standing instruction ("cite the internal ticket number") without
     * owning the agent's whole prompt.
     */
    systemPromptPrefix: z.string().optional(),
    modelAlias: ModelAliasSchema.optional(),
    answerMode: AnswerModeSchema.optional(),
    tools: AgentToolGrantSchema.partial().optional(),
    retrieval: AgentRetrievalSchema.optional(),
    memory: AgentMemorySchema.optional(),
    /**
     * A CEILING the agent may lower, never raise. The policy states the platform's limit and
     * this narrows it per agent; a body asking for more than its policy allows gets the
     * policy's number, silently and by construction (`min` at resolution).
     */
    budget: AgentBudgetSchema.optional(),
    delegation: AgentDelegationSchema.partial().optional(),
    /** Free-form, carried through resolution untouched, for operator bookkeeping. */
    meta: z.record(z.string(), z.unknown()).optional()
  })
  .strict()

export type AgentBody = z.infer<typeof AgentBodySchema>

/**
 * The merged, complete agent the runtime drives under.
 *
 * `systemPrompt` is required here and nowhere else. There is no platform base prompt to
 * fall back on: an agent is minimally a prompt, so a definition that resolves without one
 * is rejected before any model call rather than silently running as a generic assistant.
 *
 * Note the asymmetry with `AgentBodySchema`: `retrieval` and `humanInLoop` are still whole
 * here even though the body no longer carries their tuning. That is the point of a policy
 * plane — the runtime still needs one complete answer for "how deep does search go" and
 * "how long does an approval stay open", it just no longer takes it from something a maker
 * can write. `policy` names where those values came from, so a resolved agent explains
 * itself and a run snapshot can record it (AGM-100/102).
 */
export const ResolvedAgentSchema = z.object({
  agentId: AgentIdSchema,
  systemPrompt: z.string().min(1),
  systemPromptPrefix: z.string(),
  modelAlias: ModelAliasSchema,
  answerMode: AnswerModeSchema,
  tools: AgentToolGrantSchema,
  retrieval: z.object({
    strategy: z.enum(['hybrid', 'semantic', 'lexical']),
    topK: z.number().int().positive(),
    minScore: z.number().min(0),
    scopeIds: z.array(z.uuid())
  }),
  memory: z.object({ read: z.boolean(), write: z.enum(['off', 'propose', 'auto']) }),
  budget: z.object({
    maxSteps: z.number().int().positive(),
    maxModelCalls: z.number().int().positive(),
    maxToolCalls: z.number().int().positive(),
    maxDelegations: z.number().int().nonnegative(),
    maxCostUsd: z.number().nonnegative(),
    maxWallClockMs: z.number().int().positive()
  }),
  /**
   * Approval settings, entirely policy-supplied.
   *
   * `additionalApprovalRisks` is always empty in V1 and kept in the shape on purpose: the
   * platform floor (`APPROVAL_REQUIRED_RISKS`) is the only thing that decides whether a risk
   * class needs a human, and nothing below the policy plane may add to it (AGM-083). The
   * field exists so a future admin-plane escalation has somewhere to land — not so a
   * definition can claim one.
   */
  humanInLoop: z.object({
    additionalApprovalRisks: z.array(RiskClassSchema),
    maxApprovalRounds: z.number().int().positive(),
    approvalTtlMs: z.number().int().positive()
  }),
  delegation: z.object({
    remoteAgents: z.array(z.string()),
    maxDepth: z.number().int().min(0),
    maxParallel: z.number().int().min(1)
  }),
  /** The RuntimePolicy this resolution was measured against (AGM-100/102/103). */
  policy: ResolvedPolicyRefSchema,
  meta: z.record(z.string(), z.unknown()),
  /** Which layers actually contributed, least-specific first. Shown in the admin UI. */
  layers: z.array(z.object({ scopeId: z.string(), version: z.number().int() })),
  /**
   * True when a layer failed validation and was dropped to keep the agent usable. The run
   * still happens; the operator sees that it happened on degraded configuration.
   */
  degraded: z.boolean()
})
export type ResolvedAgent = z.infer<typeof ResolvedAgentSchema>

// ---------------------------------------------------------------------------
// Admin API shapes
// ---------------------------------------------------------------------------

export const AgentDefinitionSchema = z.object({
  agentId: AgentIdSchema,
  scopeId: AgentScopeIdSchema,
  displayName: z.string(),
  description: z.string(),
  body: AgentBodySchema,
  version: z.number().int(),
  enabled: z.boolean(),
  ownerId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
})
export type AgentDefinition = z.infer<typeof AgentDefinitionSchema>

export const UpsertAgentDefinitionRequestSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  body: AgentBodySchema,
  enabled: z.boolean().optional(),
  changeNote: z.string().max(500).optional()
})
export type UpsertAgentDefinitionRequest = z.infer<typeof UpsertAgentDefinitionRequestSchema>

export const AgentSummarySchema = z.object({
  agentId: AgentIdSchema,
  displayName: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  scopes: z.array(z.string()),
  version: z.number().int(),
  updatedAt: z.iso.datetime()
})
export type AgentSummary = z.infer<typeof AgentSummarySchema>

export const AgentListResponseSchema = z.object({ items: z.array(AgentSummarySchema) })
export type AgentListResponse = z.infer<typeof AgentListResponseSchema>

/** What an agent resolves to, without running it. It is the same for everyone who can use it (PRD-26 D-23). */
export const TestResolveAgentRequestSchema = z.object({}).strict()

export const AgentVersionSchema = z.object({
  version: z.number().int(),
  scopeId: z.string(),
  changeNote: z.string(),
  changedBy: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  body: AgentBodySchema
})
export type AgentVersion = z.infer<typeof AgentVersionSchema>
